import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { TotalResults } from '@shared/components/ui/TotalResults';
import type { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { WalletsAgGridTable, WalletsBreadcrumb } from '../display';
import { ErrorState, LoadingState } from '../feedback';
import { useWalletsInfinite } from '../../hooks/useWalletsInfinite';
import { useWalletsColumns } from '../../hooks/useWalletsColumns';
import { Wallet } from '../../models/Wallets';
import { FiltersSection, CreateWalletButton, WalletNavigationDropdown } from '../interaction';
import { useNavigate } from 'react-router-dom';
import type { WalletsAdvancedFilters, WalletsFilters } from '../../adapters/WalletsApiAdapter';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { Main as WalletDetailMain } from '@modules/Wallets/Wallet/components';

const getColumnId = (columnId?: string, accessorKey?: string | number) => {
  if (columnId) return columnId;
  if (typeof accessorKey === 'string') return accessorKey;
  return String(accessorKey ?? '');
};

const matchesSearch = (wallet: Wallet, term: string) => {
  if (!term) return true;
  const normalized = term.toLowerCase();

  const searchableValues = [wallet.id?.toString(), wallet.description, wallet.team?.name, wallet.currency];

  return searchableValues.some((value) => value?.toString().toLowerCase().includes(normalized));
};

const MULTI_VALUE_OPERATORS = new Set(['in', 'not_in']);
const VOID_OPERATORS = new Set(['is_null', 'is_not_null']);
const NUMERIC_COLUMNS = new Set(['balance_total']);

const buildAdvancedFiltersPayload = (conditions: FilterConditionOutput[]): WalletsAdvancedFilters | undefined => {
  const filters = conditions
    .map((condition) => {
      const { column, action } = condition;
      const rawValue = condition.value as unknown;

      if (!column || !action) {
        return null;
      }

      if (VOID_OPERATORS.has(action)) {
        return {
          column,
          operator: action,
        };
      }

      if (MULTI_VALUE_OPERATORS.has(action)) {
        let valuesArray: Array<string | number> = [];

        if (Array.isArray(rawValue)) {
          valuesArray = rawValue as Array<string | number>;
        } else if (typeof rawValue === 'string') {
          valuesArray = rawValue
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
        } else if (rawValue !== undefined && rawValue !== null && rawValue !== '') {
          valuesArray = [rawValue as string | number];
        }

        if (!valuesArray.length) {
          return null;
        }

        const processedArray = NUMERIC_COLUMNS.has(column)
          ? valuesArray
              .map((item) => {
                const numericValue = typeof item === 'number' ? item : Number(item);
                return Number.isNaN(numericValue) ? null : numericValue;
              })
              .filter((item): item is number => item !== null)
          : valuesArray;

        if (!processedArray.length) {
          return null;
        }

        return {
          column,
          operator: action,
          value: processedArray,
        };
      }

      const singleValue = Array.isArray(rawValue) ? rawValue[0] : (rawValue as string | number | undefined);

      if (singleValue === undefined || singleValue === null || singleValue === '') {
        return null;
      }

      if (NUMERIC_COLUMNS.has(column)) {
        const numericValue = typeof singleValue === 'number' ? singleValue : Number(singleValue);
        if (Number.isNaN(numericValue)) {
          return null;
        }

        return {
          column,
          operator: action,
          value: numericValue,
        };
      }

      return {
        column,
        operator: action,
        value: singleValue,
      };
    })
    .filter((filter): filter is WalletsAdvancedFilters['filters'][number] => filter !== null);

  if (!filters.length) {
    return undefined;
  }

  return {
    filters,
    logic: 'AND',
  };
};

export const Main = () => {
  const { t } = useTranslation('walletsModule');
  const navigate = useNavigate();
  const { getTeamType } = usePermissions();
  const teamType = getTeamType();
  const isExternalUser = teamType === 'tenant_external';
  const hasEverLoaded = useRef(false);

  const { params: queryParams, updateParams } = useQueryParams({
    search: { type: 'string', default: '' },
    filters: { type: 'json', default: [] as FilterConditionOutput[] },
  });

  const searchValue = queryParams.search ?? '';
  const filterConditions = useMemo(
    () => (Array.isArray(queryParams.filters) ? queryParams.filters : []),
    [queryParams.filters],
  );

  const advancedFilters = useMemo(() => buildAdvancedFiltersPayload(filterConditions), [filterConditions]);

  const walletFilters = useMemo<WalletsFilters>(
    () =>
      advancedFilters
        ? {
            advanced_filters: advancedFilters,
          }
        : {},
    [advancedFilters],
  );

  const { data, isPending, isError, error, refetch, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useWalletsInfinite(walletFilters);

  const handleViewWallet = useCallback(
    (wallet: Wallet) => {
      navigate(`/wallets/${wallet.id}`);
    },
    [navigate],
  );

  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  const { tanStackColumns, filteredColumnDefs } = useWalletsColumns({
    onViewWallet: handleViewWallet,
    selectedColumns,
  });

  const columnIds = useMemo(
    () =>
      tanStackColumns.map((column) =>
        getColumnId(
          column.id as string,
          Object.prototype.hasOwnProperty.call(column, 'accessorKey')
            ? (column as { accessorKey?: string | number }).accessorKey
            : undefined,
        ),
      ),
    [tanStackColumns],
  );

  useEffect(() => {
    setSelectedColumns((current) => {
      if (current.length === columnIds.length && current.every((id) => columnIds.includes(id))) {
        return current;
      }
      return columnIds;
    });
  }, [columnIds]);

  const handleSearch = useCallback(
    (value: string) => {
      if (value === searchValue) return;
      updateParams({ search: value });
    },
    [searchValue, updateParams],
  );

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      if (JSON.stringify(conditions) === JSON.stringify(filterConditions)) return;
      updateParams({ filters: conditions });
    },
    [filterConditions, updateParams],
  );

  const handleColumnsChange = useCallback((columnsIds: string[]) => {
    setSelectedColumns(columnsIds);
  }, []);

  const wallets = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data]);

  const totalFromApi = data?.pages[0]?.meta.total;

  // 🎯 Para usuarios tenant o tenant_3pl, mostrar la lista normal
  // For tenant or tenant_3pl users, show the normal list
  const filteredWallets = useMemo(
    () => wallets.filter((wallet) => matchesSearch(wallet, searchValue)),
    [wallets, searchValue],
  );

  // 🎯 Si es usuario external, mostrar vista de detalle de su billetera
  // If external user, show detail view of their wallet
  if (isExternalUser) {
    if (isPending) {
      return <LoadingState message={t('loading.wallets')} />;
    }

    if (isError || !data) {
      return <ErrorState error={error ?? new Error(t('errors.generic'))} onRetry={refetch} title={t('errors.title')} />;
    }

    // Obtener el ID de la primera (y única) billetera del usuario external
    // Get the ID of the first (and only) wallet of the external user
    const externalWalletId = wallets[0]?.id;

    if (!externalWalletId) {
      return <ErrorState error={new Error(t('errors.noWallet'))} onRetry={refetch} title={t('errors.title')} />;
    }

    // Renderizar la vista de detalle con el ID de la billetera (sin breadcrumb para usuarios external)
    // Render the detail view with the wallet ID (without breadcrumb for external users)
    return <WalletDetailMain walletId={externalWalletId} hideBreadcrumb={true} />;
  }

  // Nunca early-return desde aquí — el header y los filtros deben permanecer visibles siempre
  if (!isPending) hasEverLoaded.current = true;

  const hasActiveFilters = Boolean(searchValue?.trim()) || filterConditions.length > 0;
  const isInitialLoad = isPending && !hasEverLoaded.current;
  const isTableLoading = isFetching && !isFetchingNextPage;

  const renderTableArea = () => {
    if (isInitialLoad) {
      return <LoadingState />;
    }
    if (isError) {
      return <ErrorState error={error ?? new Error(t('errors.generic'))} onRetry={refetch} title={t('errors.title')} />;
    }
    return (
      <>
        <div
          className={`transition-opacity duration-300 ${isTableLoading ? 'opacity-50 pointer-events-none' : 'opacity-100'}`}
        >
          <WalletsAgGridTable
            wallets={filteredWallets}
            columnDefs={filteredColumnDefs}
            fetchNextPage={() => {
              void fetchNextPage();
            }}
            hasNextPage={hasNextPage ?? false}
            isFetchingNextPage={isFetchingNextPage}
          />
        </div>
        {filteredWallets.length === 0 && !isTableLoading && (
          <EmptyView
            type={hasActiveFilters ? 'noResults' : 'emptyState'}
            icon={EMPTY_VIEW_ICONS.ORDERS}
            title={hasActiveFilters ? t('noResults') : t('emptyState')}
          />
        )}
      </>
    );
  };

  return (
    <>
      <WalletsBreadcrumb />
      <div className="px-6 pt-4 flex flex-col gap-4">
        <div className="shrink-0 absolute right-4 top-2 flex items-center gap-2">
          <WalletNavigationDropdown />
          <CreateWalletButton />
        </div>
        <div className="mb-2">
          <div className="relative flex items-center justify-between flex-wrap gap-3">
            <FiltersSection
              columns={tanStackColumns as ColumnDef<Wallet>[]}
              selectedColumns={selectedColumns}
              fixedColumns={['actions']}
              onColumnsChange={handleColumnsChange}
              searchValue={searchValue}
              onSearch={handleSearch}
              filterConditions={filterConditions}
              onFilterChange={handleFilterChange}
            />
            <div className="flex flex-col items-end ml-auto">
              <TotalResults
                total={searchValue?.trim() ? filteredWallets.length : (totalFromApi ?? filteredWallets.length)}
              />
            </div>
          </div>
        </div>

        {renderTableArea()}
      </div>
    </>
  );
};
